import React from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { ClipboardCheck, Pencil, Trash2, ChevronDown, ChevronUp, Plus, Sparkles } from 'lucide-react';
import { QCItemRow } from './QCItemRow';

export const QCTemplateCard = ({
  tpl,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onToggleActive,
  onEditItem,
  onDeleteItem,
  onToggleRequired,
  onReorder,
  onAddItem,
  serviceName,
}) => {
  const requiredCount = (tpl.items || []).filter(i => i.is_required).length;
  const optionalCount = (tpl.items || []).length - requiredCount;

  return (
    <Card
      className={`border-0 shadow-md overflow-hidden transition-all ${!tpl.is_active ? 'opacity-60' : ''}`}
      data-testid={`template-card-${tpl.id}`}
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-zinc-50/50 transition-colors"
        onClick={onToggleExpand}
      >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          tpl.is_default ? 'bg-[#FFF7ED] text-[#F56F05]' : 'bg-zinc-100 text-zinc-500'
        }`}>
          <ClipboardCheck className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-zinc-900 text-sm">{tpl.name}</h3>
            {tpl.is_default && <Badge className="bg-[#F56F05] text-white text-[10px]">Default</Badge>}
            {!tpl.is_active && <Badge variant="outline" className="text-zinc-400 text-[10px]">Inactive</Badge>}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-zinc-500">
            {serviceName ? (
              <span className="flex items-center gap-1"><Sparkles className="h-3 w-3" />{serviceName}</span>
            ) : (
              <span>All services (fallback)</span>
            )}
            <span>{tpl.items?.length || 0} items</span>
            <span className="text-red-500">{requiredCount} required</span>
            {optionalCount > 0 && <span className="text-zinc-400">{optionalCount} optional</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(tpl); }}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-[#F56F05] hover:bg-[#FFF7ED] transition-colors"
            data-testid={`edit-template-${tpl.id}`}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(tpl); }}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            data-testid={`delete-template-${tpl.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
          {isExpanded ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
        </div>
      </div>

      {/* Expanded: Items List */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="border-t border-zinc-100 px-5 py-4 bg-zinc-50/30">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-wide">Checklist Items</p>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={tpl.is_active}
                    onCheckedChange={() => onToggleActive(tpl)}
                    data-testid={`toggle-active-${tpl.id}`}
                  />
                  <span className="text-xs text-zinc-500">{tpl.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>

              {(tpl.items || []).length === 0 ? (
                <div className="text-center py-6 text-zinc-400">
                  <p className="text-sm">No checklist items yet</p>
                </div>
              ) : (
                <Reorder.Group
                  axis="y"
                  values={tpl.items || []}
                  onReorder={(newOrder) => onReorder(tpl.id, newOrder)}
                  className="space-y-1.5"
                >
                  {(tpl.items || []).map(item => (
                    <QCItemRow
                      key={item.id}
                      item={item}
                      onEdit={(i) => onEditItem(i, tpl.id)}
                      onDelete={(i) => onDeleteItem(i, tpl.id)}
                      onToggleRequired={(i, val) => onToggleRequired(i, tpl.id, val)}
                    />
                  ))}
                </Reorder.Group>
              )}

              <Button
                size="sm"
                variant="outline"
                className="mt-3 w-full border-dashed"
                onClick={() => onAddItem(tpl.id)}
                data-testid={`add-item-${tpl.id}`}
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};

export default QCTemplateCard;
